import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiRequestError } from "../../api/client";
import {
  CREDIT_FEATURE_LABELS,
  CREDIT_RATE_ROWS,
  PRICING,
  professionLabel,
} from "../../../shared/constants";
import { CreditEntryPill, creditSigned, formatDateTime, personName } from "./ui";

interface AdminUser {
  id: string;
  email: string;
  role: string;
  last_name: string | null;
  first_name: string | null;
  office_name: string | null;
  profession: string | null;
}

interface LedgerEntry {
  id: string;
  entry_type: string;
  delta: number;
  reason: string | null;
  feature: string | null;
  ref_type: string | null;
  ref_id: string | null;
  expires_at: string | null;
  created_at: string;
  actor_email: string | null;
  actor_last_name: string | null;
  actor_first_name: string | null;
}

interface CreditsResponse {
  user: AdminUser;
  balance: number;
  entries: LedgerEntry[];
}

export function AdminCreditsPage() {
  const [customers, setCustomers] = useState<AdminUser[]>([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState<CreditsResponse | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [amount, setAmount] = useState<number>(3000);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ users: AdminUser[] }>("/api/admin/users")
      .then((res) => setCustomers(res.users.filter((u) => u.role === "customer")))
      .catch((e) => setError(e.message))
      .finally(() => setLoadingUsers(false));
  }, []);

  const loadDetail = useCallback((userId: string) => {
    if (!userId) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    api
      .get<CreditsResponse>(`/api/admin/credits/${userId}`)
      .then((res) => setDetail(res))
      .catch((e) => setError(e.message))
      .finally(() => setLoadingDetail(false));
  }, []);

  useEffect(() => {
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const submit = async (mode: "grant" | "adjust", sign: 1 | -1) => {
    if (!selectedId) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      setError("理由は必須です");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError("正の金額を入力してください");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.post<{ balance: number }>("/api/admin/credits", {
        user_id: selectedId,
        delta: sign * amount,
        mode,
        reason: trimmed,
      });
      setNotice(
        `${mode === "grant" ? "付与" : "減算"}しました。現在残高: ${res.balance.toLocaleString("ja-JP")}cr`,
      );
      setReason("");
      loadDetail(selectedId);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : "処理に失敗しました");
    } finally {
      setBusy(false);
    }
  };

  const selectedLabel = useMemo(() => {
    const u = customers.find((c) => c.id === selectedId);
    if (!u) return "";
    return u.office_name ?? personName(u.last_name, u.first_name, u.email);
  }, [customers, selectedId]);

  return (
    <section>
      <div className="admin-page-head">
        <div>
          <div className="admin-eyebrow">運営管理</div>
          <h1>クレジット管理</h1>
          <p className="muted">
            顧客のクレジット残高・履歴の確認と、手動付与・減算を行います（1cr = 1円）。
          </p>
        </div>
        <div className="head-actions">
          <label className="filter-field">
            <span>顧客を選択</span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={loadingUsers}
            >
              <option value="">選択してください</option>
              {customers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.office_name ?? personName(u.last_name, u.first_name, u.email)}（
                  {u.email}）
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-info">{notice}</div>}

      {!selectedId ? (
        <div className="card">
          <p className="muted">顧客を選択すると残高・履歴が表示されます。</p>
        </div>
      ) : loadingDetail || !detail ? (
        <div className="card">
          <p className="muted">読み込み中...</p>
        </div>
      ) : (
        <>
          <div className="detail-grid">
            <div className="card balance-card">
              <div className="stat-label">現在の残高</div>
              <div className="balance-value">
                {detail.balance.toLocaleString("ja-JP")}
                <span className="stat-unit">cr</span>
              </div>
              <div className="muted small">
                {selectedLabel} ・{" "}
                {professionLabel(detail.user.profession)}
              </div>
            </div>

            <div className="card">
              <h2>手動付与 / 減算</h2>
              <div className="form">
                <label>
                  金額（cr）
                  <input
                    type="number"
                    min={1}
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                  />
                </label>
                <label>
                  理由<span className="req">必須</span>
                  <input
                    type="text"
                    placeholder="例: キャンペーン付与 / 誤付与分の調整"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <div className="btn-row">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => submit("grant", 1)}
                    disabled={busy}
                  >
                    ＋ 付与
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => submit("adjust", -1)}
                    disabled={busy}
                  >
                    － 減算
                  </button>
                </div>
                <p className="muted small">
                  付与の有効期限は付与から{PRICING.creditExpiryMonths}ヶ月です。
                </p>
              </div>
            </div>
          </div>

          <div className="card">
            <h2>クレジット履歴</h2>
            {detail.entries.length === 0 ? (
              <p className="muted">履歴がありません。</p>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>日時</th>
                    <th>種別</th>
                    <th>内容</th>
                    <th>クレジット数</th>
                    <th>担当者</th>
                    <th>備考</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.entries.map((e) => (
                    <tr key={e.id}>
                      <td>{formatDateTime(e.created_at)}</td>
                      <td>
                        <CreditEntryPill type={e.entry_type} />
                      </td>
                      <td>
                        {e.reason ??
                          (e.feature
                            ? (CREDIT_FEATURE_LABELS[e.feature] ?? e.feature)
                            : "—")}
                      </td>
                      <td className={e.delta < 0 ? "neg" : "pos"}>
                        {creditSigned(e.delta)}
                      </td>
                      <td>
                        {e.actor_email
                          ? personName(e.actor_last_name, e.actor_first_name, e.actor_email)
                          : "システム"}
                      </td>
                      <td className="muted small">
                        {e.feature
                          ? (CREDIT_FEATURE_LABELS[e.feature] ?? e.feature)
                          : e.ref_type
                            ? `${e.ref_type}`
                            : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      <div className="card">
        <h2>クレジット消費レート</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>機能</th>
              <th>消費</th>
            </tr>
          </thead>
          <tbody>
            {CREDIT_RATE_ROWS.map((r) => (
              <tr key={r.feature}>
                <td>{r.feature}</td>
                <td>{r.cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted small">
          チャージ単位: {PRICING.creditChargeOptions.map((a) => a.toLocaleString("ja-JP")).join(" / ")} 円（1cr = 1円）
        </p>
      </div>
    </section>
  );
}
